import { Segment, ActiveStats, SUPPORTED_TAG_TYPES } from './IVbC2paContract';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
export type segmentType = 'video' | 'audio' | 'contentVideo';

export class VbC2paStatsReporter {
	private segmentMap: Record<segmentType, Map<string, Segment>> = {
		video: new Map<string, Segment>(),
		audio: new Map<string, Segment>(),
		contentVideo: new Map<string, Segment>()
	};
	private overallVerified: Record<segmentType, boolean> = {
		video: true,
		audio: true,
		contentVideo: true
	};
	private errorSegments: Record<segmentType, Segment[]> = {
		video: [],
		audio: [],
		contentVideo: []
	};
	private currentStatsSubject$ = new BehaviorSubject<ActiveStats | null>(null);
	private lastCurrentTimeInSec: number = 0;

	private lastActiveSegmentName: Record<string, string | null> = {
		audio: null,
		video: null,
		contentVideo: null
	};

	private currentTimeSub: Subscription;
	constructor() { }

	public initPlayerCurrentTimeObservable(currentTime$: Observable<number>): void {
		this.currentTimeSub?.unsubscribe();
		this.currentTimeSub = currentTime$.subscribe((t) => {
			const seconds = t / 1000;
			this.lastCurrentTimeInSec = seconds;
			this.updateActiveStats(seconds);
		});
	}

	/** Observable with current active segment + overallVerified */
	public get currentStats$(): Observable<ActiveStats | null> {
		return this.currentStatsSubject$.asObservable();
	}

	public reportMp4Stats(activeStats: ActiveStats): void {
		if (!activeStats) {
			return;
		}

		this.currentStatsSubject$.next(activeStats);
	}

	public addVerifiedFragmentToStats(type: segmentType, stats: Segment['stats']): void {
		if (!this.segmentMap[type] || this.segmentMap[type].size === 0) {
			this.segmentMap[type] = new Map();
			this.overallVerified[type] = true;
		}

		const interval = stats.interval as [number, number];
		const key = `${interval[0]}:${interval[1]}`;

		// Replace duplicate if exists
		if (this.segmentMap[type].has(key)) {
			const oldSeg = this.segmentMap[type].get(key)!;
			this.segmentMap[type].delete(key);

			if (!oldSeg.stats.verified) {
				this.overallVerified[type] = this.recomputeOverallVerified(type);
			}
		}

		const seg: Segment = { interval, stats };
		this.segmentMap[type].set(key, seg);
		if (!stats.verified) {
			this.overallVerified[type] = false;
			this.errorSegments[type] = this.errorSegments[type] || [];
			this.errorSegments[type].push(seg);
		}

		if (interval[0] <= this.lastCurrentTimeInSec && this.lastCurrentTimeInSec <= interval[1]) {
			this.updateActiveStats(this.lastCurrentTimeInSec);
		}
	}

	private recomputeOverallVerified(tag: string): boolean {
		for (const seg of this.segmentMap[tag].values()) {
			if (!seg.stats.verified) {
				return false;
			}
		}
		return true;
	}

	private updateActiveStats(currentTime: number): void {
		for (const type of SUPPORTED_TAG_TYPES) {
			const map = this.segmentMap[type];

			if (!map || map.size === 0) {
				continue;
			}

			let active: Segment['stats'] | null = null;

			for (const seg of map.values()) {
				if (seg.interval[0] <= currentTime && currentTime <= seg.interval[1]) {
					active = seg.stats;
					break;
				}
			}
			const activeSegmentName = active?.segmentName ?? null;

			if (!!this.lastActiveSegmentName[type] && this.lastActiveSegmentName[type] === activeSegmentName) {
				continue; //don't fire duplicate
			}

			//this is handle when during seeking or segment fetching takes longer time
			// we don't want to clear the active segment if we still have the last active segment
			if(!active && this.lastActiveSegmentName[type] != null) {
				console.warn('VbC2paStatsReporter: No segment found for the current player time', currentTime, 'lastActiveSegmentName:', this.lastActiveSegmentName[type], 'all Data', map);
				continue;
			}

			this.lastActiveSegmentName[type] = activeSegmentName;

			this.currentStatsSubject$.next({
				type: type as 'audio' | 'video',
				activeSegmentStats: active,
				overallVerified: this.overallVerified[type],
				errorSegments: this.errorSegments[type] || [],
				currentTime,
			});

		}
	}

	/** Clean up */
	public destroy(): void {
		this.currentTimeSub?.unsubscribe();
		this.currentStatsSubject$.next(null);
		this.lastActiveSegmentName = {
			audio: null,
			video: null,
		}
		this.lastCurrentTimeInSec = 0;
		this.segmentMap = {
			video: new Map<string, Segment>(),
			audio: new Map<string, Segment>(),
			contentVideo: new Map<string, Segment>()
		};
		this.overallVerified = {
			video: true,
			audio: true,
			contentVideo: true
		};
		this.errorSegments = {
			video: [],
			audio: [],
			contentVideo: []
		};
	}
}

export const c2paStatReporterInstance = new VbC2paStatsReporter();
